<template>
  <div class="h-myset">
    <div class="h-myset__content">
      <div class="h-myset__head">
        <h1 class="h-myset__title">My settings</h1>
        <p class="h-myset__sub">{{ subtitle }}</p>
      </div>

      <HermesEmptyState
        v-if="loadError"
        variant="error"
        title="Couldn't load your settings"
        :message="loadError"
        @retry="load"
      />

      <!-- ===== Signed out after a password change ===== -->
      <div v-else-if="signedOut" class="h-myset__card h-myset__card--done">
        <div class="h-myset__doneic">
          <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <h2 class="h-myset__cardtitle">Password changed</h2>
        <p class="h-myset__p">
          You have been signed out everywhere, including here. Sign in again with your new password.
        </p>
        <button type="button" class="h-btn h-btn--primary" @click="goToSignIn">Sign in</button>
      </div>

      <template v-else-if="!loading">
        <!-- ===== Profile ===== -->
        <section class="h-myset__card">
          <h2 class="h-myset__cardtitle">Your details</h2>

          <div class="h-myset__grid2">
            <div class="h-myset__field">
              <label class="h-myset__label" for="h-myset-first">First name</label>
              <input id="h-myset-first" v-model.trim="profile.first_name" class="h-myset__input" type="text" maxlength="255" autocomplete="given-name" />
              <p v-if="fieldError('profile', 'first_name')" class="h-myset__err">{{ fieldError('profile', 'first_name') }}</p>
            </div>
            <div class="h-myset__field">
              <label class="h-myset__label" for="h-myset-last">Last name</label>
              <input id="h-myset-last" v-model.trim="profile.last_name" class="h-myset__input" type="text" maxlength="255" autocomplete="family-name" />
              <p v-if="fieldError('profile', 'last_name')" class="h-myset__err">{{ fieldError('profile', 'last_name') }}</p>
            </div>
          </div>

          <div class="h-myset__field">
            <label class="h-myset__label" for="h-myset-email">Email</label>
            <input id="h-myset-email" v-model.trim="profile.email" class="h-myset__input" type="email" maxlength="255" autocomplete="email" />
            <p class="h-myset__hint">You sign in with this address.</p>
            <p v-if="fieldError('profile', 'email')" class="h-myset__err">{{ fieldError('profile', 'email') }}</p>
          </div>

          <div v-if="emailChanged" class="h-myset__field">
            <label class="h-myset__label" for="h-myset-emailpw">Current password</label>
            <input id="h-myset-emailpw" v-model="profile.current_password" class="h-myset__input" type="password" autocomplete="current-password" />
            <p class="h-myset__hint">Needed to change the address you sign in with.</p>
            <p v-if="fieldError('profile', 'current_password')" class="h-myset__err">{{ fieldError('profile', 'current_password') }}</p>
          </div>

          <dl class="h-myset__facts">
            <div><dt>Roles</dt><dd>{{ (account.roles || []).join(', ') || '—' }}</dd></div>
            <div v-if="account.vendor_name"><dt>Vendor</dt><dd>{{ account.vendor_name }}</dd></div>
            <div v-if="account.is_platform_admin"><dt>Access</dt><dd>Platform operator</dd></div>
            <div><dt>Last sign-in</dt><dd>{{ date(account.last_login) }}</dd></div>
          </dl>

          <p v-if="profileState.error && !profileState.field" class="h-myset__saveerr">{{ profileState.error }}</p>
          <p v-if="profileState.saved" class="h-myset__saveok">Saved.</p>

          <div class="h-myset__foot">
            <button type="button" class="h-btn h-btn--primary" :disabled="profileState.saving || !canSaveProfile" @click="saveProfile">
              {{ profileState.saving ? 'Saving…' : 'Save details' }}
            </button>
          </div>
        </section>

        <!-- ===== Password ===== -->
        <section class="h-myset__card">
          <h2 class="h-myset__cardtitle">Password</h2>
          <p class="h-myset__p">
            Changing your password signs you out everywhere, including here. You then sign in with the new one.
          </p>

          <div class="h-myset__field">
            <label class="h-myset__label" for="h-myset-cur">Current password</label>
            <input id="h-myset-cur" v-model="password.current_password" class="h-myset__input" type="password" autocomplete="current-password" />
            <p v-if="fieldError('password', 'current_password')" class="h-myset__err">{{ fieldError('password', 'current_password') }}</p>
          </div>

          <div class="h-myset__grid2">
            <div class="h-myset__field">
              <label class="h-myset__label" for="h-myset-new">New password</label>
              <input id="h-myset-new" v-model="password.password" class="h-myset__input" :type="show ? 'text' : 'password'" autocomplete="new-password" maxlength="200" />
              <p class="h-myset__hint">
                At least {{ minLength }} characters.
                <button type="button" class="h-myset__link" @click="show = !show">{{ show ? 'Hide' : 'Show' }}</button>
              </p>
              <p v-if="fieldError('password', 'password')" class="h-myset__err">{{ fieldError('password', 'password') }}</p>
            </div>
            <div class="h-myset__field">
              <label class="h-myset__label" for="h-myset-conf">New password again</label>
              <input id="h-myset-conf" v-model="password.password_confirm" class="h-myset__input" :type="show ? 'text' : 'password'" autocomplete="new-password" maxlength="200" />
              <p v-if="mismatch" class="h-myset__err">The two new passwords do not match.</p>
              <p v-else-if="fieldError('password', 'password_confirm')" class="h-myset__err">{{ fieldError('password', 'password_confirm') }}</p>
            </div>
          </div>

          <p v-if="passwordState.error && !passwordState.field" class="h-myset__saveerr">{{ passwordState.error }}</p>

          <div class="h-myset__foot">
            <button type="button" class="h-btn h-btn--primary" :disabled="passwordState.saving || !canSavePassword" @click="savePassword">
              {{ passwordState.saving ? 'Changing…' : 'Change password' }}
            </button>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import useTokenStore from "@apexl/icarus-core/src/store/useTokenStore";

// HermesMySettings — the signed-in person's own name, email and password.
// Hermes's own screen: io-user 6 removed /user/settings and ships nothing in its
// place. Reads and writes /me/profile and /me/password.
//
// After a password change the API ends every session, this one included, so the
// screen drops its tokens and sends the person to sign in again. That also proves
// the new password works before they walk away.
export default {
  name: 'HermesMySettings',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      tokens: useTokenStore(),
      loading: true,
      loadError: null,
      signedOut: false,
      show: false,
      account: {},
      profile: { first_name: '', last_name: '', email: '', current_password: '' },
      password: { current_password: '', password: '', password_confirm: '' },
      profileState: { saving: false, saved: false, error: '', field: '' },
      passwordState: { saving: false, error: '', field: '' },
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      return this.account.email || '';
    },
    minLength() {
      return this.account.password_min_length || 12;
    },
    emailChanged() {
      return (this.profile.email || '').toLowerCase() !== (this.account.email || '').toLowerCase();
    },
    profileChanged() {
      return this.profile.first_name !== this.account.first_name
        || this.profile.last_name !== this.account.last_name
        || this.emailChanged;
    },
    canSaveProfile() {
      if (!this.profileChanged) return false;
      if (!this.profile.first_name || !this.profile.last_name) return false;
      if (!/.+@.+\..+/.test(this.profile.email)) return false;
      if (this.emailChanged && !this.profile.current_password) return false;
      return true;
    },
    mismatch() {
      return !!this.password.password_confirm && this.password.password !== this.password.password_confirm;
    },
    canSavePassword() {
      return !!this.password.current_password
        && this.password.password.length >= this.minLength
        && this.password.password === this.password.password_confirm;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = null;
      try {
        const data = await this.api.get(`${this.apiBase}/me/profile`);
        this.apply(data.profile || {});
      } catch (e) {
        const status = e?.response?.status;
        this.loadError = `Failed to load your settings${status ? ` (${status})` : ''}. ${e?.response?.data?.message || e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    apply(account) {
      this.account = account;
      this.profile = {
        first_name: account.first_name || '',
        last_name: account.last_name || '',
        email: account.email || '',
        current_password: '',
      };
    },
    fieldError(form, field) {
      const state = form === 'profile' ? this.profileState : this.passwordState;
      return state.field === field ? state.error : '';
    },
    async saveProfile() {
      if (!this.canSaveProfile || this.profileState.saving) return;
      this.profileState = { saving: true, saved: false, error: '', field: '' };

      const payload = { first_name: this.profile.first_name, last_name: this.profile.last_name };
      if (this.emailChanged) {
        payload.email = this.profile.email;
        payload.current_password = this.profile.current_password;
      }

      try {
        const data = await this.api.patch(`${this.apiBase}/me/profile`, payload);
        this.apply(data.profile || {});
        this.profileState = { saving: false, saved: true, error: '', field: '' };
      } catch (e) {
        this.profileState = { saving: false, saved: false, error: this.errMsg(e), field: e?.response?.data?.field || '' };
      }
    },
    async savePassword() {
      if (!this.canSavePassword || this.passwordState.saving) return;
      this.passwordState = { saving: true, error: '', field: '' };

      try {
        await this.api.post(`${this.apiBase}/me/password`, { ...this.password });
        this.password = { current_password: '', password: '', password_confirm: '' };
        this.passwordState = { saving: false, error: '', field: '' };
        // The server has ended this session; drop the tokens that pointed at it.
        this.tokens.clearAllTokens();
        this.signedOut = true;
      } catch (e) {
        this.passwordState = { saving: false, error: this.errMsg(e), field: e?.response?.data?.field || '' };
      }
    },
    goToSignIn() {
      window.location.href = '/login';
    },
    errMsg(e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message;
      if (serverMsg) return serverMsg;
      return `Could not save${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
    },
    date(v) {
      if (!v) return 'Never';
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-myset { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-myset__content { padding: 26px 32px 64px; max-width: 760px; width: 100%; }
.h-myset__head { margin-bottom: 22px; }
.h-myset__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-myset__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }

.h-myset__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); padding: 22px 24px; margin-bottom: 18px; }
.h-myset__card--done { text-align: center; padding: 36px 24px; }
.h-myset__cardtitle { font-size: 16px; font-weight: 600; margin: 0 0 14px; }
.h-myset__p { font-size: 13.5px; line-height: 1.55; color: var(--h-text-secondary, #64748B); margin: 0 0 16px; }
.h-myset__doneic { width: 44px; height: 44px; border-radius: 12px; background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
.h-myset__doneic svg { width: 22px; height: 22px; stroke: currentColor; fill: none; stroke-width: 3; }

.h-myset__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.h-myset__field { margin-bottom: 16px; }
.h-myset__label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; }
.h-myset__hint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 6px 0 0; line-height: 1.5; }
.h-myset__err { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin: 6px 0 0; }
.h-myset__link { border: none; background: none; padding: 0; margin-left: 6px; font: inherit; font-size: 12px; color: var(--h-accent-dark, #0369A1); cursor: pointer; text-decoration: underline; }

.h-myset__input { width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A); border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box; }
.h-myset__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }

.h-myset__facts { margin: 4px 0 0; padding: 14px 0 0; border-top: 1px solid var(--h-border, #E2E8F0); display: grid; gap: 8px; }
.h-myset__facts > div { display: flex; gap: 12px; font-size: 13px; }
.h-myset__facts dt { width: 110px; flex: none; color: var(--h-text-tertiary, #94A3B8); }
.h-myset__facts dd { margin: 0; color: var(--h-text-primary, #0F172A); }

.h-myset__foot { display: flex; justify-content: flex-end; margin-top: 18px; }
.h-myset__saveerr { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 14px; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); }
.h-myset__saveok { font-size: 12.5px; color: var(--h-success-text, #047857); margin-top: 14px; padding: 10px 12px; background: var(--h-success-bg, #ECFDF5); border-radius: var(--h-radius, 6px); }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 640px) {
  .h-myset__grid2 { grid-template-columns: 1fr; }
}
</style>
